import React from 'react';
import { useTheme } from '@mui/material';
import { Indicator } from '../Indicator';
import type { IndicatorVariant } from '../Indicator';

/**
 * Variant the chip parent passes to nested `<ChipCounter>` instances so the
 * counter pill matches the chip's color. Default is `primary` for standalone
 * use; `InteractiveChip` overrides per its own variant via the provider.
 */
const ChipCounterVariantContext = React.createContext<IndicatorVariant>('primary');
export const ChipCounterVariantProvider = ChipCounterVariantContext.Provider;

export interface ChipCounterProps {
  /** Numeric or short text content of the pill (e.g. `42`, `"99+"`). */
  children: React.ReactNode;
}

/**
 * Count pill rendered inside an `InteractiveChip`.
 *
 * Wraps the pounceV2 `Indicator` component and picks up its variant from
 * `ChipCounterVariantProvider` so the pill colour tracks the chip's variant
 * automatically.
 *
 * @example
 * ```tsx
 * <InteractiveChip variant="primary" onClick={open}>
 *   <ChipLabel>Filters</ChipLabel>
 *   <ChipCounter>{count}</ChipCounter>
 * </InteractiveChip>
 * ```
 */
export const ChipCounter = React.forwardRef<HTMLDivElement, ChipCounterProps>(function ChipCounter(
  { children },
  ref,
) {
  const variant = React.useContext(ChipCounterVariantContext);
  const theme = useTheme();
  // Figma `Indicators/Colors/{variant}/Stroke/Resting`. Token tree is
  // exhaustive over IndicatorVariant — a missing path is a token-gen bug
  // we want to surface, not silently swallow.
  const strokeColor = theme.palette.componentTokens.indicators.colors[variant].stroke.resting;
  return (
    <Indicator
      ref={ref}
      variant={variant}
      size="large"
      sx={{ border: '1px solid', borderColor: strokeColor }}
    >
      {children}
    </Indicator>
  );
});
